import React, { useRef, useState, useEffect, ComponentType, useCallback } from 'react';
import Ribbon from 'components/ribbons/Ribbon';
import PaddingBox from '@uds/padding-box';
import Slider from '@uds/slider';
import { Container } from '@uds/grid';
import Equalizer from 'react-equalizer';
import { useBreakpoints } from '@uds/responsive';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import errorBoundary from 'presentation/hocs/errorBoundary';
import visibilitySet from 'presentation/hocs/visibilitySet';
import debounce from 'presentation/utils/debounce';
import isTouchDevice from 'presentation/utils/isTouchDevice';
import { useTheme } from '@uds/theme-utils';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { Product } from '@uds/product';
import { CTA } from 'core/ribbons/cta';
import { PricingStrategy } from 'presentation/components/common/ProductPrice/constants';

import { FeatureProps } from './ItemFeature';
import { VisibleItems } from './constants';
import useStylesheet, { getClassNames } from './Salesboard.stylesheet';
import Navigation from './Navigation';
import Pagination from './Pagination';
import Item, { ProductDetailsType } from './Item';
import { getMaxFeatureLimit, getVisibleItems } from './utils';

export type SalesboardProductType = {
  action?: string;
  ctas: Array<CTA>;
  endorse: boolean;
  features: Array<FeatureProps>;
  illustration?: string;
  imageUrl?: string;
  pricingStrategy: PricingStrategy;
  product: Product;
  productId: string;
  text?: ProductDetailsType;
};

interface OuterProps extends RibbonProps {
  initialScrollOffset?: number;
  products?: Array<SalesboardProductType>;
  hBaseline?: number;
  visibleFeatureLimit: number;
}

const PureSalesboard = ({
  layout,
  name,
  products = [],
  colorway,
  loading,
  hBaseline = 1,
  id,
  visibleFeatureLimit,
  initialScrollOffset = 0,
}: OuterProps) => {
  const { theme } = useTheme();
  const breakpoints = useBreakpoints();

  const visibleItems = getVisibleItems(breakpoints);
  const [activeIndex, setActiveIndex] = useState(initialScrollOffset);
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const onShowMoreClick = () => setShowMoreFeatures(!showMoreFeatures);

  // we have more items than fit on the screen
  const showPagination = products.length > visibleItems;
  // only show arrow nav if we're not a touch device
  const showNavigation = !isTouchDevice() && showPagination;

  const [scroller, scrollerRef]: [any, any] = useState(null);

  const onScroll = debounce(() => {
    // run the active index setter via scroll
    const arrayedCollection = Array(...scroller.children).map((el) => el.getBoundingClientRect().left);
    const currentVisible = arrayedCollection.findIndex((value) => value >= 0 && value < 1000);
    if (currentVisible > -1) {
      setActiveIndex(currentVisible);
    }
  }, 100);
  // take an index and scroll to that node, used for desktop/button nav
  const scrollToNode = useCallback(
    (idx: number) => {
      if (!scroller || !scroller.scrollTo || !scroller.children) {
        return;
      }

      // check index is with bounds
      const index = idx < 0 ? 0 : Math.min(idx, products.length - visibleItems);

      const node = scroller.children[index];

      if (!node) {
        return;
      }

      const offset = node.getBoundingClientRect();

      scroller.scrollTo({
        left: offset.width * index,
        top: offset.top,
        behavior: 'smooth',
      });
      setActiveIndex(index);
    },
    [products.length, scroller, visibleItems],
  );

  const nodes = (() => {
    const nodes = [];
    for (let i = 0; i < 12; i += 1) {
      // TODO: This should mostly likely be refactored, but not in the scope of upgrading eslint-config.
      // This seems to work, so not changing now.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      nodes.push(useRef());
    }
    return nodes;
  })();
  const getNodes = () => {
    const parsedNodes = [] as Array<any>;
    nodes.forEach((node) => {
      if (node.current) {
        parsedNodes.push(node.current);
      }
    });
    return parsedNodes;
  };

  const onNavigationClick = (direction: string) => {
    if (direction === 'next') {
      scrollToNode(activeIndex + 1);
    }

    if (direction === 'prev') {
      scrollToNode(activeIndex - 1);
    }
  };

  const { styles } = useStylesheet({
    theme,
  });

  const maxFeatureLimit = getMaxFeatureLimit(products);

  const activeVisibleFeatureLimit = findResponsiveItem(breakpoints, visibleFeatureLimit, {
    supportNull: true,
  });

  const hasMoreFeatures = activeVisibleFeatureLimit !== null && activeVisibleFeatureLimit < maxFeatureLimit;

  let hasFeaturedProduct = false;
  let i = 0;
  const productsLength = products.length;

  while (hasFeaturedProduct === false && i < productsLength) {
    if (products[i].endorse) {
      hasFeaturedProduct = true;
    }
    i += 1;
  }
  // on first load, scroll to the correct node
  useEffect(() => {
    scrollToNode(initialScrollOffset);
  }, [scroller, scrollToNode, initialScrollOffset]);

  const classNames = getClassNames({
    showNavigation,
  });

  return (
    <Ribbon id={id} layout={layout} name={name} colorway={colorway} loading={loading} type={Type.SALESBOARD}>
      <div style={styles.root}>
        <Container fluid={showPagination}>
          <Equalizer byRow={false} nodes={() => getNodes()}>
            <Slider
              ref={scrollerRef}
              onScroll={onScroll}
              visibleItems={Object.values(VisibleItems)}
              customScrollSnap="start"
            >
              {products.map((item, i) => (
                <Item
                  key={`salesboard_item_${id}`.concat(String(i))}
                  id={id}
                  index={i}
                  descRef={nodes[i]}
                  hasFeaturedProduct={hasFeaturedProduct}
                  colorway={colorway}
                  hBaseline={hBaseline}
                  onShowMoreClick={onShowMoreClick}
                  showMoreFeatures={showMoreFeatures}
                  hasMoreFeatures={hasMoreFeatures}
                  maxFeatureLimit={maxFeatureLimit}
                  visibleFeatureLimit={activeVisibleFeatureLimit}
                  {...item}
                />
              ))}
            </Slider>
          </Equalizer>
        </Container>
        <Container>
          {showPagination && (
            <div style={styles.clickZone} role="button" aria-hidden="true" onClick={() => onNavigationClick('next')} />
          )}
          {(showPagination || showNavigation) && (
            <PaddingBox className={classNames.pageNavControls}>
              {showPagination && (
                <Pagination
                  colorway={colorway}
                  visibleItems={visibleItems}
                  activeIndex={activeIndex}
                  itemCount={products.length}
                />
              )}
              {showNavigation && <Navigation colorway={colorway} onNavigationClick={onNavigationClick} />}
            </PaddingBox>
          )}
        </Container>
      </div>
    </Ribbon>
  );
};

PureSalesboard.displayName = 'Salesboard';

export default errorBoundary(visibilitySet(PureSalesboard)) as ComponentType<OuterProps>;
